import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PenSquare, UploadCloud, Copy, ArrowUpRight, Clock, Layers } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function useTimeAgo() {
  const { t } = useTranslation();
  return (dateStr) => {
    if (!dateStr) return "";
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return t("common.justNow");
    if (diff < 3600) return t("common.minutesAgo", { count: Math.floor(diff / 60) });
    if (diff < 86400) return t("common.hoursAgo", { count: Math.floor(diff / 3600) });
    return t("common.daysAgo", { count: Math.floor(diff / 86400) });
  };
}

function RecentDesignCard({ design, onOpen, onDuplicate }) {
  const { t } = useTranslation();
  const timeAgo = useTimeAgo();
  return (
    <div className="bhf-card group overflow-hidden transition-shadow hover:shadow-lg" data-testid={`recent-design-${design.id}`}>
      <div className="aspect-[4/3] w-full overflow-hidden bg-slate-100">
        {design.previewImage?.url ? (
          <img src={fileUrl(design.previewImage)} alt={design.designNumber} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-slate-400">{t("common.noPreview")}</div>
        )}
      </div>
      <div className="p-3.5">
        <div className="flex items-center justify-between">
          <span className="font-mono-bhf text-[11px] font-medium text-indigo-600">{design.designNumber}</span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">V{design.version}</span>
        </div>
        <div className="mt-1 truncate text-sm font-semibold text-slate-800">{design.productName || t("home.untitledProduct")}</div>
        <div className="mt-0.5 flex items-center justify-between text-xs text-slate-500">
          <span className="truncate">{design.customer || t("common.noCustomer")} · {design.brandName || "—"}</span>
        </div>
        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-400">
          <Clock className="h-3 w-3" /> {timeAgo(design.updatedAt)}
        </div>
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => onOpen(design.id)} data-testid={`open-design-${design.id}`}>
            <ArrowUpRight className="h-3.5 w-3.5" /> {t("common.open")}
          </Button>
          <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => onDuplicate(design.id)} data-testid={`duplicate-design-${design.id}`}>
            <Copy className="h-3.5 w-3.5" /> {t("common.duplicate")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/designs?limit=8").then(({ data }) => setDesigns(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleDuplicate = async (id) => {
    try {
      const { data } = await api.post(`/designs/${id}/duplicate`, {});
      toast.success(t("home.duplicatedAs", { n: data.designNumber }));
      navigate(`/designer/${data.id}`);
    } catch (e) {
      toast.error(t("home.duplicateFailed"));
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-8 py-10" data-testid="home-page">
      <div className="mb-9">
        <h1 className="font-display text-3xl font-700 text-slate-900">{t("home.title")}</h1>
        <p className="mt-1.5 text-sm text-slate-500">{t("home.subtitle")}</p>
      </div>

      <div className="grid gap-5 sm:grid-cols-3">
        <div className="bhf-card relative overflow-hidden p-7" data-testid="create-new-entry">
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100" />
          <div className="relative">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bhf-btn-primary text-white">
              <PenSquare className="h-5 w-5" />
            </div>
            <h2 className="mt-4 font-display text-lg font-700 text-slate-900">{t("home.createTitle")}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{t("home.createDesc1")}<br />{t("home.createDesc2")}</p>
            <Button className="bhf-btn-primary mt-5 border-0 text-white" onClick={() => navigate("/designer")} data-testid="start-new-design-button">
              {t("home.startNew")}
            </Button>
          </div>
        </div>

        <div className="bhf-card relative overflow-hidden p-7" data-testid="edit-existing-entry">
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-slate-100" />
          <div className="relative">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-800 text-white">
              <UploadCloud className="h-5 w-5" />
            </div>
            <h2 className="mt-4 font-display text-lg font-700 text-slate-900">{t("home.editTitle")}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{t("home.editDesc1")}<br />{t("home.editDesc2")}</p>
            <Button variant="outline" className="mt-5" onClick={() => navigate("/upload-existing")} data-testid="upload-existing-card-button">
              {t("home.uploadExisting")}
            </Button>
          </div>
        </div>

        <div className="bhf-card relative overflow-hidden p-7" data-testid="batch-generate-entry">
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100" />
          <div className="relative">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600 text-white">
              <Layers className="h-5 w-5" />
            </div>
            <h2 className="mt-4 font-display text-lg font-700 text-slate-900">{t("home.batchTitle")}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{t("home.batchDesc1")}<br />{t("home.batchDesc2")}</p>
            <Button className="mt-5 bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => navigate("/batch")} data-testid="start-batch-generation-button">
              {t("home.startBatch")}
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-12">
        <h3 className="font-display text-base font-700 text-slate-800">{t("home.recent")}</h3>
        {loading ? (
          <div className="mt-4 text-sm text-slate-400">{t("common.loading")}</div>
        ) : designs.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400" data-testid="no-recent-designs">
            {t("home.noRecent")}
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {designs.map((d) => (
              <RecentDesignCard key={d.id} design={d} onOpen={(id) => navigate(`/designer/${id}`)} onDuplicate={handleDuplicate} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
